/**
 * Client half of contextual ad targeting (Wave 4c, T1 + T4).
 *
 * - T1: builds the visitor's context dimensions (device, first-touch traffic,
 *   funnel stage, page family, segment) and appends them to the
 *   `/api/monetization/offer` query. No PII, no AI, no network.
 * - T4: keeps a per-arm fatigue ledger in localStorage (unclicked views since
 *   the last click, last click time) and sends it as two short params. The
 *   thresholds (`fatigueImpressions`, `cooldownHours`, …) live server-side in
 *   the `monetization-settings` payload; the client only reports counts.
 *
 * Every storage access is wrapped in try/catch: private mode, disabled
 * storage or a quota error degrade to "no context" (server maps to `unknown`)
 * and never break the ad request. Market is resolved server-side from geo.
 *
 * Dependency-free apart from the vocabulary module, so it adds no weight to
 * the ad bundles.
 */

import { RECENT_APPLY_CLICK_KEY } from "@/lib/analytics/hooks";
import { BANDIT_ARM_PARAM, CATEGORY_HINT_PARAM } from "./context-bandit";
import {
	CLICKED_QUERY_PARAM,
	CONTEXT_QUERY_PARAMS,
	FATIGUE_QUERY_PARAM,
} from "./targeting-dimensions";

const FATIGUE_KEY = "ld_ad_arm_fatigue_v1";
const SESSION_CTX_KEY = "ld_ad_ctx_v1";
/** Last job category the visitor viewed (a slug or `cat_*` id). */
const LAST_CATEGORY_KEY = "ld_ad_last_cat_v1";
const CATEGORY_SHAPE = /^[a-z][a-z0-9_-]{1,40}$/;
const MAX_ARMS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

type ArmFatigue = { n: number; c?: number };
type FatigueLedger = Record<string, ArmFatigue>;
type SessionCtx = { traffic: string; pv: number; lastPath: string };

function readLedger(): FatigueLedger {
	try {
		const raw = window.localStorage.getItem(FATIGUE_KEY);
		if (!raw) return {};
		const parsed: unknown = JSON.parse(raw);
		if (typeof parsed !== "object" || parsed === null) return {};
		const out: FatigueLedger = {};
		for (const [id, v] of Object.entries(parsed)) {
			if (typeof v !== "object" || v === null) continue;
			const n = (v as { n?: unknown }).n;
			const c = (v as { c?: unknown }).c;
			out[id] = {
				n: typeof n === "number" && n >= 0 ? n : 0,
				...(typeof c === "number" ? { c } : {}),
			};
		}
		return out;
	} catch {
		return {};
	}
}

function writeLedger(ledger: FatigueLedger): void {
	try {
		// Keep the most recently touched arms only, so the ledger stays small.
		const entries = Object.entries(ledger).slice(-MAX_ARMS);
		window.localStorage.setItem(
			FATIGUE_KEY,
			JSON.stringify(Object.fromEntries(entries)),
		);
	} catch {
		// Storage unavailable — fatigue simply does not apply for this visitor.
	}
}

/** One viewable impression of `platform` (called from the impression beacon). */
export function recordArmImpression(platform: string): void {
	if (typeof window === "undefined") return;
	const ledger = readLedger();
	const prev = ledger[platform] ?? { n: 0 };
	delete ledger[platform];
	ledger[platform] = { ...prev, n: prev.n + 1 };
	writeLedger(ledger);
}

/** A click on `platform`: resets its unclicked-view count, starts cooldown. */
export function recordArmClick(
	platform: string,
	now: number = Date.now(),
): void {
	if (typeof window === "undefined") return;
	const ledger = readLedger();
	delete ledger[platform];
	ledger[platform] = { n: 0, c: now };
	writeLedger(ledger);
}

const SEARCH_HOSTS = [
	"google.",
	"bing.",
	"duckduckgo.",
	"yahoo.",
	"yandex.",
	"baidu.",
	"ecosia.",
	"search.brave.",
];
const SOCIAL_HOSTS = [
	"facebook.",
	"instagram.",
	"t.co",
	"twitter.",
	"x.com",
	"linkedin.",
	"lnkd.in",
	"tiktok.",
	"youtube.",
	"reddit.",
	"threads.",
	"whatsapp.",
	"telegram.",
	"t.me",
];
const SOCIAL_SOURCES = [
	"facebook",
	"fb",
	"instagram",
	"ig",
	"twitter",
	"x",
	"linkedin",
	"tiktok",
	"youtube",
	"reddit",
	"threads",
	"whatsapp",
	"telegram",
];

/**
 * First-touch traffic source from UTM params + referrer. Pure, exported for
 * tests. `selfHost` is our own host: an internal referrer is not a source.
 */
export function classifyTraffic(
	search: string,
	referrer: string,
	selfHost: string,
): string {
	const q = new URLSearchParams(search);
	const medium = (q.get("utm_medium") ?? "").toLowerCase();
	const source = (q.get("utm_source") ?? "").toLowerCase();
	if (["cpc", "ppc", "paid", "paid_social", "display", "cpm"].includes(medium))
		return "paid";
	if (medium === "email" || source === "email" || source === "newsletter")
		return "email";
	if (medium === "social" || SOCIAL_SOURCES.includes(source)) return "social";
	let host = "";
	try {
		host = referrer ? new URL(referrer).hostname.toLowerCase() : "";
	} catch {
		host = "";
	}
	if (!host || host === selfHost) return source ? "referral" : "direct";
	if (SEARCH_HOSTS.some((h) => host.includes(h))) return "organic_search";
	if (SOCIAL_HOSTS.some((h) => host.includes(h))) return "social";
	return "referral";
}

/** Route → the targeting page-family vocabulary. Pure, exported for tests. */
export function pageFamilyOf(pathname: string): string {
	const parts = pathname.split("/").filter(Boolean);
	if (parts[0] === "en" || parts[0] === "id") parts.shift();
	const [first, second] = parts;
	if (first === "jobs") return second ? "job" : "jobs";
	if (first === "remote-jobs") return "jobs";
	if (first === "blog") return "blog";
	if (first === "seeker" || first === "hunt") return "hunt";
	if (
		first === "applications" ||
		first === "saved" ||
		first === "resume" ||
		first === "settings" ||
		first === "onboarding" ||
		first === "dashboard"
	)
		return "dashboard";
	return "other";
}

/** Funnel stage from session depth + signals. Pure, exported for tests. */
export function funnelStage(input: {
	pageviews: number;
	pageFamily: string;
	segment?: string;
	lastApplyAt?: number | null;
	now: number;
}): string {
	if (input.lastApplyAt && input.now - input.lastApplyAt < DAY_MS)
		return "applied_today";
	if (input.segment?.startsWith("free")) return "signed_up_free";
	if (input.pageviews >= 5 || input.pageFamily === "hunt") return "engaged";
	if (input.pageviews >= 2) return "browsing";
	return "new";
}

function readSessionCtx(): SessionCtx {
	const path = window.location.pathname;
	let ctx: SessionCtx | null = null;
	try {
		const raw = window.sessionStorage.getItem(SESSION_CTX_KEY);
		if (raw) {
			const p: unknown = JSON.parse(raw);
			if (
				typeof p === "object" &&
				p !== null &&
				typeof (p as SessionCtx).traffic === "string" &&
				typeof (p as SessionCtx).pv === "number"
			) {
				ctx = p as SessionCtx;
			}
		}
	} catch {
		ctx = null;
	}
	if (!ctx) {
		ctx = {
			traffic: classifyTraffic(
				window.location.search,
				document.referrer,
				window.location.hostname.toLowerCase(),
			),
			pv: 1,
			lastPath: path,
		};
	} else if (ctx.lastPath !== path) {
		ctx = { ...ctx, pv: ctx.pv + 1, lastPath: path };
	}
	try {
		window.sessionStorage.setItem(SESSION_CTX_KEY, JSON.stringify(ctx));
	} catch {
		// Storage unavailable — this request still gets its context.
	}
	return ctx;
}

function readLastApplyAt(): number | null {
	try {
		const raw = window.localStorage.getItem(RECENT_APPLY_CLICK_KEY);
		const n = raw ? Number(raw) : Number.NaN;
		return Number.isFinite(n) ? n : null;
	} catch {
		return null;
	}
}

function isMobileDevice(): boolean {
	try {
		if (window.matchMedia?.("(max-width: 767px)").matches) return true;
	} catch {
		// fall through to the UA check
	}
	return /Mobi|Android|iPhone|iPod/i.test(navigator.userAgent);
}

/** Category from a `/remote-jobs/<category>` path, else null. Pure. */
export function categoryFromPath(pathname: string): string | null {
	const parts = pathname.split("/").filter(Boolean);
	if (parts[0] === "en" || parts[0] === "id") parts.shift();
	if (parts[0] !== "remote-jobs" || !parts[1]) return null;
	return CATEGORY_SHAPE.test(parts[1]) ? parts[1] : null;
}

/**
 * Remember the category this page is about (the request's `category`, else
 * the path) and return the one to send as `cx_cat`: this page's, else the
 * last one the visitor viewed. Kept in localStorage only.
 */
function rememberedCategory(params: URLSearchParams): string | null {
	const raw =
		params.get("category") ?? categoryFromPath(window.location.pathname);
	const here = raw && CATEGORY_SHAPE.test(raw) ? raw : null;
	try {
		if (here) {
			window.localStorage.setItem(LAST_CATEGORY_KEY, here);
			return here;
		}
		const stored = window.localStorage.getItem(LAST_CATEGORY_KEY);
		return stored && CATEGORY_SHAPE.test(stored) ? stored : null;
	} catch {
		return here;
	}
}

/**
 * Append the visitor's targeting context + fatigue ledger to an offer
 * request. Safe to call on every request: when targeting is disabled the
 * server ignores these params entirely.
 */
export function appendAdContextParams(
	params: URLSearchParams,
	opts: {
		segment?: string;
		pageFamily?: string;
		/** `affiliate-context-bandit` variant (`test` | `control`), when resolved. */
		banditArm?: unknown;
	} = {},
	now: number = Date.now(),
): void {
	if (typeof window === "undefined") return;
	try {
		if (opts.banditArm === "test" || opts.banditArm === "control") {
			params.set(BANDIT_ARM_PARAM, opts.banditArm);
		}
		const category = rememberedCategory(params);
		if (category) params.set(CATEGORY_HINT_PARAM, category);
		const session = readSessionCtx();
		const pageFamily =
			opts.pageFamily ?? pageFamilyOf(window.location.pathname);
		params.set(
			CONTEXT_QUERY_PARAMS.device,
			isMobileDevice() ? "mobile" : "desktop",
		);
		params.set(CONTEXT_QUERY_PARAMS.traffic, session.traffic);
		params.set(
			CONTEXT_QUERY_PARAMS.funnel,
			funnelStage({
				pageviews: session.pv,
				pageFamily,
				segment: opts.segment,
				lastApplyAt: readLastApplyAt(),
				now,
			}),
		);
		params.set(CONTEXT_QUERY_PARAMS.page_family, pageFamily);
		if (opts.segment) params.set(CONTEXT_QUERY_PARAMS.segment, opts.segment);

		const ledger = readLedger();
		const seen: string[] = [];
		const clicked: string[] = [];
		for (const [id, f] of Object.entries(ledger)) {
			if (f.n > 0) seen.push(`${id}:${f.n}`);
			if (f.c !== undefined) {
				const hours = Math.max(0, (now - f.c) / 3_600_000);
				clicked.push(`${id}:${hours.toFixed(1)}`);
			}
		}
		if (seen.length > 0) params.set(FATIGUE_QUERY_PARAM, seen.join(","));
		if (clicked.length > 0) params.set(CLICKED_QUERY_PARAM, clicked.join(","));
	} catch {
		// Context is best-effort; the ad request goes out regardless.
	}
}
